/**
 * Algolia Search Client with Optimized Caching
 *
 * This file provides the Algolia search client initialization with:
 * - LRU memory cache for responses (prevents unbounded growth)
 * - localStorage cache with TTL for requests (5 minutes)
 * - Automatic cleanup to prevent quota exceeded errors
 * - Fallback caching strategies
 * - Algolia Insights integration for click tracking
 *
 * Re-exports index configuration from AlgoliaIndices.ts for convenience.
 * For index configuration only, see AlgoliaIndices.ts
 */

import { liteClient as algoliasearch } from 'algoliasearch/lite';
import type { LiteClient } from 'algoliasearch/lite';
import type { Cache, CacheEvents } from '@algolia/client-common';
import aa from 'search-insights';
import type { InsightsClient } from 'search-insights';

// Re-export types and indices from configuration file
export type {
  MainLocale,
  InvestorLocale,
  Locale,
  AlgoliaIndices,
} from './AlgoliaIndices';

import { getAlgoliaUserToken as getUserToken } from './AlgoliaIndices';

export {
  ALGOLIA_INDICES,
  getGlobalIndex,
  getBlogIndex,
  getEventsIndex,
  getResourcesIndex,
  getProductsIndex,
  getInvestorIndex,
  getAlgoliaUserToken,
} from './AlgoliaIndices';

// =============================================================================
// Enhanced Cache Implementation
// =============================================================================

/**
 * Configuration for cache size limits
 */
const CACHE_CONFIG = {
  // Maximum number of entries in memory cache
  MAX_MEMORY_ENTRIES: 100,
  // Maximum size of localStorage cache in bytes (~2.5MB)
  MAX_STORAGE_SIZE: 2.5 * 1024 * 1024,
  // Time to live for cache entries (5 minutes)
  TTL: 5 * 60 * 1000,
  // Prefix for localStorage keys
  STORAGE_KEY: 'algolia-search',
};

/**
 * Create an LRU (Least Recently Used) in-memory cache
 * Automatically evicts old entries when cache is full
 *
 * Performance benefits:
 * - O(1) access time
 * - Prevents unbounded memory growth
 * - Keeps most relevant queries cached
 *
 * @param maxEntries - Maximum number of cache entries (default: 100)
 */
function createLRUMemoryCache(maxEntries = CACHE_CONFIG.MAX_MEMORY_ENTRIES): Cache {
  const cache = new Map<string, { value: unknown; timestamp: number }>();

  const evictOldestIfNeeded = () => {
    if (cache.size >= maxEntries) {
      // Find and remove oldest entry
      let oldestKey: string | null = null;
      let oldestTime = Infinity;

      cache.forEach((entry, key) => {
        if (entry.timestamp < oldestTime) {
          oldestTime = entry.timestamp;
          oldestKey = key;
        }
      });

      if (oldestKey) {
        cache.delete(oldestKey);
      }
    }
  };

  return {
    get: <TValue>(
      key: Record<string, unknown> | string,
      defaultValue: () => Promise<TValue>,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _events?: CacheEvents<TValue>
    ): Promise<TValue> => {
      const cacheKey = typeof key === 'string' ? key : JSON.stringify(key);
      const cached = cache.get(cacheKey);

      if (cached !== undefined) {
        // Update timestamp for LRU (move to end)
        cache.set(cacheKey, { ...cached, timestamp: Date.now() });
        return Promise.resolve(cached.value as TValue);
      }

      // Cache miss
      const result = defaultValue();
      result.then((val) => {
        evictOldestIfNeeded();
        cache.set(cacheKey, { value: val, timestamp: Date.now() });
      }).catch(() => {
        // Ignore errors in cache population
      });
      return result;
    },

    set: <TValue>(key: Record<string, unknown> | string, value: TValue): Promise<TValue> => {
      const cacheKey = typeof key === 'string' ? key : JSON.stringify(key);
      evictOldestIfNeeded();
      cache.set(cacheKey, { value, timestamp: Date.now() });
      return Promise.resolve(value);
    },

    delete: (key: Record<string, unknown> | string): Promise<void> => {
      const cacheKey = typeof key === 'string' ? key : JSON.stringify(key);
      cache.delete(cacheKey);
      return Promise.resolve();
    },

    clear: (): Promise<void> => {
      cache.clear();
      return Promise.resolve();
    },
  };
}

/**
 * Get current localStorage usage in bytes
 */
function getLocalStorageSize(keyPrefix: string): number {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${keyPrefix}:`)) {
        const value = localStorage.getItem(key);
        if (value) {
          // Count both key and value bytes
          total += key.length + value.length;
        }
      }
    }
  } catch {
    return 0;
  }
  return total;
}

/**
 * Clean up old cache entries from localStorage
 * Removes expired entries and oldest entries if size limit exceeded
 */
function cleanupLocalStorageCache(keyPrefix: string, ttl: number, maxSize: number): void {
  try {
    const entries: Array<{ key: string; timestamp: number; size: number }> = [];

    // Collect all cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${keyPrefix}:`)) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            const timestamp = parsed.timestamp || 0;
            const now = Date.now();

            // Remove expired entries
            if (now - timestamp > ttl) {
              localStorage.removeItem(key);
            } else {
              entries.push({
                key,
                timestamp,
                size: key.length + value.length,
              });
            }
          } catch {
            // Invalid entry, remove it
            localStorage.removeItem(key);
          }
        }
      }
    }

    // If still over size limit, remove oldest entries
    const currentSize = entries.reduce((sum, entry) => sum + entry.size, 0);
    if (currentSize > maxSize) {
      // Sort by timestamp (oldest first)
      entries.sort((a, b) => a.timestamp - b.timestamp);

      let removed = 0;
      for (const entry of entries) {
        localStorage.removeItem(entry.key);
        removed += entry.size;

        // Stop when we're under the limit
        if (currentSize - removed < maxSize * 0.8) {
          break;
        }
      }
    }
  } catch {
    // If cleanup fails, clear all cache entries as last resort
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(`${keyPrefix}:`))
        .forEach(k => localStorage.removeItem(k));
    } catch {
      // Give up if we can't clean up
    }
  }
}

/**
 * Create an optimized localStorage cache with size management
 *
 * Performance improvements over original:
 * - Automatic cache size management
 * - Proactive cleanup of old entries
 * - Better error handling
 * - Prevents localStorage quota exceeded errors
 *
 * @param options Configuration options for the cache
 */
function createOptimizedLocalStorageCache(options: {
  key?: string;
  ttl?: number;
  maxSize?: number;
  debug?: boolean;
} = {}): Cache {
  const {
    key = CACHE_CONFIG.STORAGE_KEY,
    ttl = CACHE_CONFIG.TTL,
    maxSize = CACHE_CONFIG.MAX_STORAGE_SIZE,
    debug = false,
  } = options;

  // Run cleanup on initialization
  if (typeof window !== 'undefined') {
    // Defer cleanup to avoid blocking initialization
    setTimeout(() => cleanupLocalStorageCache(key, ttl, maxSize), 100);
  }

  return {
    get: <TValue>(
      cacheKey: Record<string, unknown> | string,
      defaultValue: () => Promise<TValue>,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _events?: CacheEvents<TValue>
    ): Promise<TValue> => {
      const storageKey = typeof cacheKey === 'string' ? cacheKey : JSON.stringify(cacheKey);
      const fullKey = `${key}:${storageKey}`;

      try {
        const item = localStorage.getItem(fullKey);
        if (!item) {
          // eslint-disable-next-line no-console
          if (debug) console.log('[Algolia Cache] MISS:', fullKey.substring(0, 80));

          const result = defaultValue();
          result.then((val) => {
            try {
              // Check size before storing
              const itemToStore = JSON.stringify({
                value: val,
                timestamp: Date.now(),
              });

              // If adding this would exceed limit, cleanup first
              if (getLocalStorageSize(key) + itemToStore.length > maxSize) {
                cleanupLocalStorageCache(key, ttl, maxSize);
              }

              localStorage.setItem(fullKey, itemToStore);
              // eslint-disable-next-line no-console
              if (debug) console.log('[Algolia Cache] STORED:', fullKey.substring(0, 80));
            } catch (e) {
              // If storage fails, try cleanup and retry once
              cleanupLocalStorageCache(key, ttl, maxSize);
              try {
                const itemToStore = JSON.stringify({
                  value: val,
                  timestamp: Date.now(),
                });
                localStorage.setItem(fullKey, itemToStore);
              } catch {
                // Give up if still failing
                // eslint-disable-next-line no-console
                if (debug) console.warn('[Algolia Cache] Storage failed after cleanup');
              }
            }
          }).catch(() => {
            // Ignore fetch errors
          });

          return result;
        }

        const parsed = JSON.parse(item);
        const { value, timestamp } = parsed;

        // Check expiration
        if (Date.now() - timestamp > ttl) {
          // eslint-disable-next-line no-console
          if (debug) console.log('[Algolia Cache] EXPIRED:', fullKey.substring(0, 80));
          localStorage.removeItem(fullKey);

          const result = defaultValue();
          result.then((val) => {
            try {
              const itemToStore = JSON.stringify({
                value: val,
                timestamp: Date.now(),
              });
              localStorage.setItem(fullKey, itemToStore);
            } catch {
              // Ignore storage errors
            }
          }).catch(() => {
            // Ignore fetch errors
          });

          return result;
        }

        // Cache hit!
        // eslint-disable-next-line no-console
        if (debug) console.log('[Algolia Cache] HIT:', fullKey.substring(0, 80));
        return Promise.resolve(value as TValue);
      } catch {
        // On any error, just fetch fresh data
        return defaultValue();
      }
    },

    set: <TValue>(cacheKey: Record<string, unknown> | string, value: TValue): Promise<TValue> => {
      const storageKey = typeof cacheKey === 'string' ? cacheKey : JSON.stringify(cacheKey);
      const fullKey = `${key}:${storageKey}`;

      try {
        const item = JSON.stringify({
          value,
          timestamp: Date.now(),
        });

        // Check size before storing
        if (getLocalStorageSize(key) + item.length > maxSize) {
          cleanupLocalStorageCache(key, ttl, maxSize);
        }

        localStorage.setItem(fullKey, item);
      } catch {
        // Fail silently
      }
      return Promise.resolve(value);
    },

    delete: (cacheKey: Record<string, unknown> | string): Promise<void> => {
      const storageKey = typeof cacheKey === 'string' ? cacheKey : JSON.stringify(cacheKey);
      const fullKey = `${key}:${storageKey}`;

      try {
        localStorage.removeItem(fullKey);
      } catch {
        // Fail silently
      }
      return Promise.resolve();
    },

    clear: (): Promise<void> => {
      try {
        Object.keys(localStorage)
          .filter(k => k.startsWith(`${key}:`))
          .forEach(k => localStorage.removeItem(k));
      } catch {
        // Fail silently
      }
      return Promise.resolve();
    },
  };
}

/**
 * Create a fallback cache that tries multiple strategies
 */
function createFallbackCache(primary: Cache, fallback: Cache): Cache {
  return {
    get: async <TValue>(
      key: Record<string, unknown> | string,
      defaultValue: () => Promise<TValue>,
      events?: CacheEvents<TValue>
    ): Promise<TValue> => {
      try {
        return await primary.get(key, defaultValue, events);
      } catch {
        return fallback.get(key, defaultValue, events);
      }
    },

    set: async <TValue>(key: Record<string, unknown> | string, value: TValue): Promise<TValue> => {
      // Try to set in both, but don't fail if one fails
      await Promise.allSettled([
        primary.set(key, value),
        fallback.set(key, value),
      ]);
      return value;
    },

    delete: async (key: Record<string, unknown> | string): Promise<void> => {
      await Promise.allSettled([
        primary.delete(key),
        fallback.delete(key),
      ]);
    },

    clear: async (): Promise<void> => {
      await Promise.allSettled([
        primary.clear(),
        fallback.clear(),
      ]);
    },
  };
}

// =============================================================================
// Optimized Search Client
// =============================================================================

/**
 * Singleton instance of the optimized search client
 */
let optimizedClientInstance: LiteClient | null = null;

/**
 * Initialize Algolia search client with optimized caching
 *
 * Improvements over original:
 * - LRU memory cache prevents unbounded growth
 * - localStorage cache with size management
 * - Automatic cleanup of old entries
 * - Better error handling
 *
 * @returns LiteClient instance with enhanced caching
 */
export const initOptimizedAlgoliaSearch = (): LiteClient => {
  // Return existing instance (singleton pattern)
  if (optimizedClientInstance) {
    return optimizedClientInstance;
  }

  const appId = process.env.NEXT_PUBLIC_ALGOLIA_ID;
  const apiKey = process.env.NEXT_PUBLIC_ALGOLIA_KEY;

  if (!appId) {
    throw new Error('Missing NEXT_PUBLIC_ALGOLIA_ID environment variable');
  }
  if (!apiKey) {
    throw new Error('Missing NEXT_PUBLIC_ALGOLIA_KEY environment variable');
  }

  // Create optimized cache instances
  const memoryCache = createLRUMemoryCache(CACHE_CONFIG.MAX_MEMORY_ENTRIES);
  const localStorageCache = createOptimizedLocalStorageCache({
    key: CACHE_CONFIG.STORAGE_KEY,
    ttl: CACHE_CONFIG.TTL,
    maxSize: CACHE_CONFIG.MAX_STORAGE_SIZE,
    debug: false,
  });
  const fallbackCache = createFallbackCache(localStorageCache, memoryCache);

  // Initialize client with optimized caching
  optimizedClientInstance = algoliasearch(appId, apiKey, {
    responsesCache: memoryCache,
    requestsCache: fallbackCache,
  });

  return optimizedClientInstance;
};

/**
 * For backward compatibility, export as default init function
 */
export const initAlgoliaSearch = initOptimizedAlgoliaSearch;

// =============================================================================
// Algolia Insights Client
// =============================================================================

/**
 * Initialize Algolia Insights for click and conversion tracking
 *
 * This enables:
 * - Click tracking on search results
 * - Conversion tracking
 * - User behavior analytics
 * - Search relevance improvements
 *
 * @returns InsightsClient instance
 */
export const initAlgoliaInsights = (): InsightsClient => {
  const appId = process.env.NEXT_PUBLIC_ALGOLIA_ID;
  const apiKey = process.env.NEXT_PUBLIC_ALGOLIA_KEY;

  if (!appId) {
    throw new Error('Missing NEXT_PUBLIC_ALGOLIA_ID environment variable');
  }
  if (!apiKey) {
    throw new Error('Missing NEXT_PUBLIC_ALGOLIA_KEY environment variable');
  }

  // Initialize insights client
  aa('init', {
    appId,
    apiKey,
    useCookie: true, // Enable cookie for user tracking
    partial: true, // Allow multiple init calls without warning
  });

  // Set user token if available
  const userToken = getUserToken();
  if (userToken) {
    aa('setUserToken', userToken);
  }

  return aa;
};

/**
 * Get the initialized Algolia Insights client
 * Safe to call multiple times, insights library handles singleton pattern
 *
 * @returns InsightsClient instance
 */
export const getAlgoliaInsights = (): InsightsClient => {
  initAlgoliaInsights();
  return aa;
};
